import React from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import {
    Box,
    Container,
    Heading,
    Text,
    Button,
    Link,
    VStack,
    SimpleGrid,
    Image,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";

export default function Professionals() {
    return (
        <Box bg="offwhite.100" minH="100vh">
            <Helmet>
                <title>Stop collating Hogan, Wave and OPQ packs | BetaPersona</title>
                <meta name="description" content="You interpret. You should not be the one collating. For in-house occupational psychologists, assessment practices, and executive coaches who still run a battery." />
            </Helmet>

            <Container maxW="container.xl" py={{ base: 12, md: 20 }}>
                <VStack spacing={12} align="stretch">
                    <Box>
                        <Heading as="h1" size="2xl" mb={6}>
                            You interpret. You should not be the one collating.
                        </Heading>
                        <Text fontSize="xl" color="ink.500">
                            You have BPS or EFPA certification. You are qualified to interpret psychometric, 360 and performance assessments. You advise employers, coach executives, or run batteries for clients.
                        </Text>
                        <Text fontSize="xl" color="ink.500" mt={4} mb={8}>
                            You should not be the one logging into three publisher portals, downloading five PDFs, and copy-pasting scale scores into Word. The collating takes longer than the interpreting.
                        </Text>
                        <Image
                            src="/images/op-7654451.jpeg"
                            alt="Professional consulting session. Photo: Pavel Danilyuk, Pexels"
                            borderRadius="md"
                            w="full"
                            h="auto"
                            objectFit="cover"
                            aspectRatio="16/9"
                        />
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                        <Box bg="white" p={8} borderRadius="md" shadow="sm">
                            <Heading as="h3" size="md" mb={4}>
                                In-house occupational psychologists
                            </Heading>
                            <Text>
                                You already specify Hogan and Psytech. After an agency shortlist the work is: recover the selection files, map them to the organisation's competencies, write something a hiring manager can use, and still be available when the decision is close.
                            </Text>
                            <Text mt={4}>
                                Same store later for 360 and performance — internal candidates and development, not the agency slate. Selection and development are one file, not two vendors with two retention habits.
                            </Text>
                            <Text mt={4} fontWeight="600">
                                The collating should not be your job. You should advise. We will collate.
                            </Text>
                        </Box>

                        <Box bg="white" p={8} borderRadius="md" shadow="sm">
                            <Heading as="h3" size="md" mb={4}>
                                Assessment practices
                            </Heading>
                            <Text>
                                You run batteries for client organizations. Each client has their own competency framework. Each client wants white-labelled reports. Each client needs managed access: who can see raw publisher reports, who can see derived packs.
                            </Text>
                            <Text mt={4}>
                                You have multiple clients. You have multiple publisher accounts. You have multiple frameworks. You are collating, not advising.
                            </Text>
                            <Text mt={4} fontWeight="600">
                                BetaPersona: one repository across your clients. White-labelled client portals. Packs mapped to their frameworks. You advise. We collate.
                            </Text>
                        </Box>

                        <Box bg="white" p={8} borderRadius="md" shadow="sm">
                            <Heading as="h3" size="md" mb={4}>
                                Executive coaches who still run a battery
                            </Heading>
                            <Text>
                                You coach senior leaders. You still run a psychometric battery as part of your engagement: Hogan, MBTI, EQi, or bespoke instruments. You need to store the results, apply access policy, and generate a pack the client organization can use.
                            </Text>
                            <Text mt={4} fontWeight="600">
                                If you run a battery, BetaPersona is for you. If you are coaching-only with no assessments, this is not your door.
                            </Text>
                        </Box>
                    </SimpleGrid>

                    <Box>
                        <Heading as="h2" size="xl" mb={6}>
                            What this is
                        </Heading>
                        <VStack align="start" spacing={6}>
                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    Repository with managed access
                                </Heading>
                                <Text>
                                    Psychometric, 360 and performance assessments in one store. You decide who can see what. Client organizations see derived packs, not raw publisher scale scores. Audit log records who viewed what, when. GDPR-aligned: you are data controller (or processor if working for a client), BetaPersona is sub-processor.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    Live integrations
                                </Heading>
                                <Text>
                                    Fire assessments from one login. Live integration with your chosen publishers e.g. Psytech. No logging into multiple publisher portals, no chasing email links, no pulling PDFs by hand. Results land in the repository automatically.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    Business-ready packs
                                </Heading>
                                <Text>
                                    Generate a pack derived from publisher reports and mapped to the client's competency framework. You provide the interpretation and advice. We provide the collated pack with scale scores, normative data, and framework mapping already done. The hiring manager or executive sponsor can use it without a psychometric or test-user qualification.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    White-labelled for assessment practices
                                </Heading>
                                <Text>
                                    Client portals carry your practice's brand, not BetaPersona. Your clients log into a portal that looks like you, with your logo, your color scheme, your domain. One repository across all your clients. One system for managed access. You advise. We collate.
                                </Text>
                            </Box>
                        </VStack>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            What this is not
                        </Heading>
                        <VStack align="start" spacing={4}>
                            <Box>
                                <Text fontWeight="600">Not a test publisher</Text>
                                <Text>
                                    We do not replace GeneSys, Hogan, SHL or Thomas. We sit beside them. We connect to your existing publisher accounts, hold the file, and generate the derived pack. The publisher still owns the test library, the normative data, and the scale-level scoring.
                                </Text>
                            </Box>
                            <Box>
                                <Text fontWeight="600">Not AI making up psychometrics</Text>
                                <Text>
                                    The pack is derived from publisher scores and the client's competency framework. We do not use AI to generate scale scores or invent normative data. The publisher provides the psychometric assessment. BetaPersona provides the repository, access policy, and derived pack.
                                </Text>
                            </Box>
                            <Box>
                                <Text fontWeight="600">Not for coaching-only practices</Text>
                                <Text>
                                    If you coach executives but do not run a psychometric battery, this is not your system. BetaPersona is for assessment storage, access policy, and pack generation. If you do not assess, you do not need this.
                                </Text>
                            </Box>
                            <Box>
                                <Text fontWeight="600">Not a clinical psychology tool</Text>
                                <Text>
                                    BetaPersona is for occupational assessment: recruitment, promotion, development, succession planning, leadership coaching. We do not process clinical-psychology data, therapeutic assessments, or health records. If you work in clinical practice, this is not your system.
                                </Text>
                            </Box>
                        </VStack>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            Ethics: qualified test use and data protection
                        </Heading>
                        <Text mb={4}>
                            <strong>British Psychological Society and European Federation of Psychologists' Associations standards apply.</strong>
                        </Text>
                        <Text mb={4}>
                            You are the qualified user. You decide which instruments to use. You interpret the results. You advise the client. BetaPersona is not a substitute for BPS or EFPA qualified test use.
                        </Text>
                        <Text mb={4}>
                            BetaPersona is the repository and report-generation system. We store the assessment files, apply your access policy, and generate the derived pack. We do not interpret. The occupational psychologist interprets. We remove the collating.
                        </Text>
                        <Text mb={4}>
                            <strong>GDPR Article 9: special-category data.</strong>
                        </Text>
                        <Text mb={4}>
                            Psychometric assessment data is special-category personal data. If you work in-house for an employer, the employer is data controller. If you run an assessment practice, you are data controller (or processor, depending on your contract with the client organization). BetaPersona is data processor or sub-processor.
                        </Text>
                        <Text mb={4}>
                            Data processing agreements cover storage, access, generation, audit logs, sub-processors, security measures, and data subject rights. The person assessed has rights to access, rectification, and erasure. The qualified professional ensures lawful processing and qualified test use.
                        </Text>
                        <Link as={ReactRouterLink} to="/security-and-data" color="navy.500" fontWeight="600">
                            Security & data →
                        </Link>
                    </Box>

                    <Box bg="navy.500" color="white" p={10} borderRadius="md" textAlign="center">
                        <Heading as="h2" size="xl" mb={6}>
                            Talk to us
                        </Heading>
                        <Text fontSize="lg" mb={8}>
                            We will walk you through the repository, integrations, access policy, and pack generation. In-house occupational psychologists: bring your HRBP or talent team. Assessment practices: bring a client scenario. Executive coaches: bring your assessment workflow.
                        </Text>
                        <Link
                            as={ReactRouterLink}
                            to="/contact"
                            _hover={{ textDecoration: "none" }}
                        >
                            <Button
                                bg="accent.orange"
                                color="white"
                                size="lg"
                                _hover={{ bg: "rgba(248, 100, 30, 0.85)" }}
                            >
                                Talk to us
                            </Button>
                        </Link>
                        <Text mt={4} fontSize="sm">
                            A working session with talent, HRBP or in-house occupational psychology. Assessment practices and executive coaches who still run a battery: same door.
                        </Text>
                    </Box>
                </VStack>
            </Container>
        </Box>
    );
}
